import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { filter, isEqual, keyBy, values, each } from 'lodash';
import { Button, Modal } from 'react-bootstrap';
import Fa from 'react-fontawesome';

import NumberDisplay from '../../../../components/number-display';
import UserTable from '../../../Setup/components/UserTable/UserTable';

import {
  getAllMembers,
  getAllMembersLoading,
  getAllMembersLoaded,
} from '../../TeamReducer';
import {
  fetchAvailableMembers,
  assignMember,
} from '../../TeamActions';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';

class MemberModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      containerWidth: 0,
      savedFilters: null,
      excluded: [],
      included: [],
      selectAll: false,
      currentMembers: null,
      totalMembers: null,
    };
    this.close = this.close.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.fetchMembers = this.fetchMembers.bind(this);
    this.onSelectionChanged = this.onSelectionChanged.bind(this);
    this.checkboxSelection = this.checkboxSelection.bind(this);
    this.onSelectedAllCheckbox = this.onSelectedAllCheckbox.bind(this);
    this.checkIndeterminate = this.checkIndeterminate.bind(this);
  }

  checkboxSelection(params) {
    return params && params.data && (params.data.is_member !== true);
  }

  onSelectionChanged(included, unselectedRows, savedFilters) {
    const newState = { savedFilters };

    const excluded = filter(unselectedRows, { is_member: false });

    const newIncluded = keyBy([...this.state.included, ...included], '_id');
    each(unselectedRows, (unselectedRow) => {
      delete newIncluded[unselectedRow._id];
    });

    newState.included = values(newIncluded);


    const newExcluded = keyBy([...this.state.excluded, ...excluded], '_id');
    each(included, (includeMember) => {
      delete newExcluded[includeMember._id];
    });

    newState.excluded = values(newExcluded);

    const includedChanged = !isEqual(values(keyBy(this.state.included, '_id')), values(keyBy(newIncluded, '_id')));
    const excludedChanged = !isEqual(values(keyBy(this.state.excluded, '_id')), values(keyBy(newExcluded, '_id')));


    this.setState(newState, () => {
      if (includedChanged || excludedChanged) {
        this.updateMemberCounter();
      }
    });
  }

  /** Handles selection change event when "Select All" checkbox is toggled in the grid, fetching and updating the 'selectedByAllCheckbox' array and the selection state of all grid nodes accordingly */
  onSelectedAllCheckbox(selectAll) {
    let { included } = this.state;
    if (!selectAll) {
      included = [];
    }
    this.setState({ selectAll, included }, () => {
      this.updateMemberCounter();
    });
  }

  /** Fetch whether there are selected or deselected rows in the grid */
  checkIndeterminate() {
    const { included, excluded } = this.state;
    return (excluded.length > 0 && included.length > 0);
  }

  /** Close the component, resetting the state and triggering the onClose callback, if not currently submitting */
  close() {
    if (!this.state.submitting) {
      this.setState({ status: 'active', totalMembers: null, currentMembers: null });
      this.props.onClose();
    }
  }

  /** Handle the form submission with optional countOnly parameter, updating the state and triggering the assignMember dispatch with provided parameters */
  _handleSubmit(countOnly = false) {
    const { selectAll, savedFilters, excluded, included } = this.state;
    const { team } = this.props;
    const params = {
      selectAll,
      excluded: [],
      included: [],
    };
    if (selectAll) {
      params.excluded = excluded.map((item) => item.user_id);
    } else {
      params.included = included.map((item) => item.user_id);
    }
    const p = this.props.dispatch(
      assignMember(team._id, params, { ...savedFilters, countOnly: countOnly === true })
    );
    return p;
  }

  /** Handle form submission, update the state, close the modal, and trigger the onSuccess callback with promise results */
  handleSubmit() {
    const p = this._handleSubmit(false);
    p.then(
      (results) => {
        this.setState({ selectAll: false, excluded: [], included: [] })
        this.close();
        this.props.onSuccess(results);
      },
      () => {
        // TODO: handle error
      },
    );
  }

  /** Update the current and total members count by submitting the form and handling the promise results accordingly */
  updateMemberCounter() {
    const p = this._handleSubmit(true);
    p.then(
      (results) => {
        this.setState({ currentMembers: results.totalMembers, totalMembers: results.maximumMembers });
      },
      () => {
        // TODO: handle error
      },
    );
  }

  /** Synchronize and fetch available members for the team with the specified parameters */
  fetchMembers(params) {
    const teamId = this.props.team._id;
    const p = this.props.dispatch(fetchAvailableMembers(teamId, params));
    return p;
  }

  /** Render the display for the team size based on currentMembers and totalMembers state, showing an error class if the team size exceeds the limit */
  renderMemberCounter() {

    const { currentMembers, totalMembers } = this.state;
    if (currentMembers === null || totalMembers === null) {
      return null;
    }

    const cls = (currentMembers > totalMembers) ? 'text-danger' : '';

    return (
      <div style={{ paddingTop: '5px' }} className={cls}>
        Team size: <NumberDisplay value={currentMembers} precision={0} /> of <NumberDisplay value={totalMembers} precision={0} />
      </div>
    );
  }

  /** Render the UserTable component with specified props for displaying team members' data and managing selections */
  renderTable() {
    const { included } = this.state;
    const { organization } = this.props;
    return (
      <UserTable
        selectedIds={included.map((item) => item._id)}
        hideActionColumn
        toggleSelect={this.onSelectionChanged}
        indeterminate={this.checkIndeterminate}
        showOrganizationDetailColumn={false}
        selectable
        checkboxSelection={this.checkboxSelection}
        disableShowFullScreenMode
        disableDownload
        canAddUser={false}
        height={600}
        fetchAction={this.fetchMembers}
        showDevice={false}
        showBrowser={false}
        showLoginCount={false}
        organization={organization}
        onSelectedAllCheckbox={this.onSelectedAllCheckbox}
        showSearchForm
      />
    );
  }

  /** Render the footer section with buttons for saving and canceling assignment of team members based on component's state */
  renderFooter() {
    const { submitting, included, currentMembers, totalMembers } = this.state;
    const { loading, loaded } = this.props;
    const isLoading = loading || !loaded;

    return (
      <div className='clearfix'>
        <Button
          bsStyle='link'
          className='pull-left'
          onClick={this.close}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          bsStyle='primary'
          className="pull-right"
          onClick={this.handleSubmit}
          disabled={isLoading || submitting || (included.length === 0) || (currentMembers > totalMembers)}
        >
          <Fa name={submitting ? 'spinner' : 'plus'} spin={submitting} /> {submitting ? 'Saving' : 'Assign members'}
        </Button>
        {!isLoading && <div className='pull-right'>{this.renderMemberCounter()}</div>}
      </div>
    );
  }

  /** Render the Assign Team Members modal dialog with user table and footer based on provided props */
  render() {
    return (
      <Modal show={this.props.show} onHide={this.close} keyboard={false} bsSize='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Assign Team Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this.renderTable()}
        </Modal.Body>
        <Modal.Footer>
          {this.renderFooter()}
        </Modal.Footer>
      </Modal>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    members: getAllMembers(state),
    loading: getAllMembersLoading(state),
    loaded: getAllMembersLoaded(state),
    organization: getUserCurrentOrganization(state),
  };
}

MemberModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  members: PropTypes.shape({}), // indexed by user_id
  organization: PropTypes.shape({}).isRequired,
  team: PropTypes.shape({
    _id: PropTypes.string,
    members: PropTypes.arrayOf(
      PropTypes.shape({
        owner_id: PropTypes.string,
      }),
    ),
  }).isRequired,
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};
MemberModal.defaultProps = {
  members: [],
  loading: false,
  loaded: false,
  show: false,
  onClose: () => {},
  onSuccess: () => {},
};

export default connect(mapStateToProps)(MemberModal);
